/**
 * Enhanced date utilities for exhibitions
 */

export const formatDate = (dateString) => {
  if (!dateString) return 'TBA';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric', 
    year: 'numeric' 
  });
};

export const formatLongDate = (dateString) => {
  if (!dateString) return 'TBA';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', { 
    weekday: 'long',
    month: 'long', 
    day: 'numeric', 
    year: 'numeric' 
  });
};

/**
 * Get days until a date
 */
export const getDaysUntil = (dateString) => {
  if (!dateString) return null;
  const date = new Date(dateString);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  date.setHours(0, 0, 0, 0);
  const diffTime = date - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

/**
 * Get urgency message for an exhibition
 */
export const getUrgencyMessage = (exhibition) => {
  const startDate = exhibition.start_date ? new Date(exhibition.start_date) : null;
  const endDate = exhibition.end_date ? new Date(exhibition.end_date) : null;

  if (!startDate || !endDate) return null;

  const daysUntilStart = getDaysUntil(exhibition.start_date);
  const daysUntilEnd = getDaysUntil(exhibition.end_date);

  // For upcoming exhibitions
  if (exhibition.status === 'upcoming') {
    if (daysUntilStart === 0) {
      return { text: '🎉 Opening today!', variant: 'success' };
    } else if (daysUntilStart === 1) {
      return { text: '✨ Opens tomorrow!', variant: 'success' };
    } else if (daysUntilStart > 0 && daysUntilStart <= 7) {
      return { text: `Opens in ${daysUntilStart} days`, variant: 'info' };
    } else if (daysUntilStart > 7 && daysUntilStart <= 30) {
      return { text: `Opens in ${Math.ceil(daysUntilStart / 7)} weeks`, variant: 'info' };
    }
  }

  // For current exhibitions
  if (exhibition.status === 'current') {
    if (daysUntilEnd === 0) {
      return { text: '⚠️ Last day!', variant: 'warning' };
    } else if (daysUntilEnd === 1) {
      return { text: '⚠️ Ends tomorrow!', variant: 'warning' };
    } else if (daysUntilEnd > 0 && daysUntilEnd <= 7) {
      return { text: `🎨 Last chance - ${daysUntilEnd} days left!`, variant: 'warning' };
    } else if (daysUntilEnd > 0 && daysUntilEnd <= 14) {
      return { text: `Ends in ${daysUntilEnd} days`, variant: 'info' };
    }
  }

  return null;
};

/**
 * Get exhibition duration in human-readable format
 */
export const getExhibitionDuration = (startDate, endDate) => {
  if (!startDate || !endDate) return null;
  
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffTime = Math.abs(end - start);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays === 1) return '1 day';
  if (diffDays < 7) return `${diffDays} days`;
  if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return weeks === 1 ? '1 week' : `${weeks} weeks`;
  }
  if (diffDays < 365) {
    const months = Math.floor(diffDays / 30);
    return months === 1 ? '1 month' : `${months} months`;
  }
  
  const years = Math.floor(diffDays / 365);
  const remainingMonths = Math.floor((diffDays % 365) / 30);
  if (remainingMonths === 0) {
    return years === 1 ? '1 year' : `${years} years`;
  }
  return `${years} year${years > 1 ? 's' : ''}, ${remainingMonths} month${remainingMonths > 1 ? 's' : ''}`;
};

/**
 * Get countdown text for upcoming exhibitions
 */
export const getCountdownText = (dateString) => {
  const daysUntil = getDaysUntil(dateString);
  
  if (daysUntil === null) return null;
  if (daysUntil < 0) return null;
  if (daysUntil === 0) return 'Today';
  if (daysUntil === 1) return 'Tomorrow';
  if (daysUntil <= 7) return `${daysUntil} days`;
  if (daysUntil <= 30) return `${Math.ceil(daysUntil / 7)} weeks`;
  if (daysUntil <= 365) return `${Math.ceil(daysUntil / 30)} months`;
  
  return `${Math.floor(daysUntil / 365)} years`;
};
